import styled from '@emotion/styled';
import {
  EDITOR_LEFT_TOOLBAR_GROUPS,
  EDITOR_LEFT_TOOLBAR_WIDTH,
  EDITOR_RIGHT_TOOLBAR_WIDTH,
} from 'modules/manager/ui/components/editor/toolbar-metrics';
import React from 'react';
import { theme } from 'styles/theme/theme';

/** Кнопка тулбара — `UDIconButton size="md"`, 36×36. */
const TOOL_BUTTON_SIZE = 36;

/**
 * Левый тулбар редактора на время загрузки: та же колонка 52px с теми же
 * отступами, квадраты 36px на местах кнопок и настоящие разделители между
 * группами (`EDITOR_LEFT_TOOLBAR_GROUPS`).
 */
export const EditorLeftToolbarSkeleton: React.FC = () => (
  <LeftRoot data-testid="editor-left-toolbar-skeleton" aria-hidden>
    {EDITOR_LEFT_TOOLBAR_GROUPS.map((count, group) => (
      <React.Fragment key={group}>
        {group > 0 && <Divider />}
        {Array.from({ length: count }, (_, index) => (
          <div
            key={index}
            className="skeleton-block shrink-0"
            style={{ width: TOOL_BUTTON_SIZE, height: TOOL_BUTTON_SIZE }}
            data-testid="editor-left-toolbar-skeleton-button"
          />
        ))}
      </React.Fragment>
    ))}
  </LeftRoot>
);

/** Высота строки `TemplateInfo`: 13px текста × line-height 1.5 — снята в браузере (шаг строк 27.5px). */
const INFO_ROW_HEIGHT = 19.5;

/** Ширины значений строк `TemplateInfo`: «Шаблон», «Версия», «Объекты», «Масштаб». */
const INFO_VALUE_WIDTHS = [120, 40, 24, 44];
const INFO_LABEL_WIDTHS = [52, 48, 60, 64];

/**
 * Секции инспектора без выделения — высоты сняты с шаблона «Расписание»: блоки макета,
 * логотип, настройки слотов. У других типов шаблонов набор секций другой; на канву это
 * не влияет — ширина панели та же, а сама панель прокручивается.
 */
const SECTION_HEIGHTS = [119, 168, 195];

/**
 * Правый тулбар (инспектор) на время загрузки: та же панель 308px, заголовок
 * «Инспектор», четыре строки `TemplateInfo` и блоки секций.
 */
export const EditorRightToolbarSkeleton: React.FC = () => (
  <RightRoot data-testid="editor-right-toolbar-skeleton" aria-hidden>
    {/* Заголовок «Инспектор»: 12px капсом, строка 18px. */}
    <div className="skeleton-block" style={{ width: 76, height: 18 }} />
    <NoSelection>
      <Info>
        {INFO_VALUE_WIDTHS.map((valueWidth, index) => (
          <InfoRow key={index}>
            <div className="skeleton-block" style={{ width: INFO_LABEL_WIDTHS[index], height: 12 }} />
            <div className="skeleton-block" style={{ width: valueWidth, height: 12 }} />
          </InfoRow>
        ))}
      </Info>
      {SECTION_HEIGHTS.map((height) => (
        <div key={height} className="skeleton-block w-full" style={{ height }} />
      ))}
    </NoSelection>
  </RightRoot>
);

// Рамки повторяют `Root` тулбаров: те же отступы, граница и фон (палитра редактора — известное
// исключение, см. `.claude/rules/conventions.md`).
const LeftRoot = styled.div`
  width: ${EDITOR_LEFT_TOOLBAR_WIDTH}px;
  height: 100%;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border-right: 1px solid ${theme.colors.DarkBlue100};
  background: ${theme.colors.SurfaceWhite};
  overflow: hidden;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  flex-shrink: 0;
  background: ${theme.colors.DarkBlue100};
  margin: 4px 0;
`;

const RightRoot = styled.div`
  width: ${EDITOR_RIGHT_TOOLBAR_WIDTH}px;
  height: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-left: 1px solid ${theme.colors.DarkBlue100};
  background: ${theme.colors.SurfaceWhite};
  overflow: hidden;
`;

const NoSelection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${INFO_ROW_HEIGHT}px;
`;
